/**
 * Keeps blurb cards (snippets/blurb-card.liquid) in their slot inside product sliders.
 *
 * Hello Retail (assets/featured-collections.js, hrRenderSlot) clears every <product-card> in the
 * track and appends the recommended cards after whatever is left. A blurb is not a <product-card>,
 * so it survives the sweep — but it would then sit in front of every recommendation. This observer
 * watches each track that holds a blurb and moves the blurb back to its 1-based slot
 * (data-fc-blurb-position) whenever the track's children change.
 *
 * Loop clones (data-fc-clone, made by fc-loop-carousel) are ignored both as cells and as blurbs, and
 * the stylesheet <link> the snippet emits is skipped like featured-collections.js skips it. Moving the
 * blurb fires one more mutation, which finds it already in place and stops, so there is no loop.
 *
 * MutationObserver callbacks run as microtasks, i.e. before the requestAnimationFrame in which
 * fc-loop-carousel re-measures the track after Hello Retail, so the clone padding is always built from
 * the corrected order.
 */
(() => {
  if (window.__mmBlurbCards) return;
  window.__mmBlurbCards = true;

  const NON_RENDERED_TAGS = new Set(['LINK', 'SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'META']);
  const observed = new WeakSet();

  function cells(track) {
    return Array.from(track.children).filter(
      (child) => !NON_RENDERED_TAGS.has(child.tagName) && !child.hasAttribute('data-fc-clone')
    );
  }

  function place(track) {
    const blurbs = Array.from(track.querySelectorAll(':scope > [data-fc-blurb]:not([data-fc-clone])'));

    blurbs.forEach((blurb) => {
      const position = parseInt(blurb.getAttribute('data-fc-blurb-position'), 10);
      if (!Number.isFinite(position) || position < 1) return;

      const current = cells(track);
      const others = current.filter((cell) => cell !== blurb);
      // A slot past the end of the set lands the blurb last.
      const index = Math.min(position - 1, others.length);

      if (current[index] === blurb) return;

      track.insertBefore(blurb, others[index] || null);
    });
  }

  function observe(track) {
    if (observed.has(track)) return;
    observed.add(track);

    place(track);
    new MutationObserver(() => place(track)).observe(track, { childList: true });
  }

  function init() {
    document.querySelectorAll('[data-fc-blurb]').forEach((blurb) => {
      if (blurb.parentElement) observe(blurb.parentElement);
    });
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();
